import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { cn } from '@/lib/utils';
import { Edit, FileText, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Template } from '@/modules/templates/types/templates.types';

interface TemplateCardProps {
  template: Template;
  orgSlug: string;
  canManage: boolean;
}

export function TemplateCard({ template, orgSlug, canManage }: TemplateCardProps) {
  const { t } = useTranslation();
  const statusLabel = template.available
    ? t('ORGTEMPLATES_STATUSACTIVE')
    : t('ORGTEMPLATES_STATUSDRAFT');
  const subtitle = `${template.slug}`;

  return (
    <Card className="hover:border-selise-blue/30 transition-colors rounded-lg">
      <CardContent className="p-4">
        {/* Mobile layout — icon + title + status badge, divider, then
            full-width action buttons. */}
        <div className="md:hidden space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 shrink-0 rounded-lg bg-selise-blue/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-selise-blue" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold truncate min-w-0 flex-1">{template.title}</h3>
                <Badge
                  variant={template.available ? 'success' : 'secondary'}
                  className="shrink-0 gap-1.5"
                >
                  <span
                    className={cn(
                      'size-1.5 rounded-full',
                      template.available ? 'bg-green-500' : 'bg-globe-grey'
                    )}
                  />
                  {statusLabel}
                </Badge>
              </div>
              <p className="text-sm text-globe-grey truncate">{subtitle}</p>
            </div>
          </div>

          {/* Divider — extended to card edges to feel like a section break. */}
          {(canManage || template.available) && <div className="-mx-4 border-t border-border" />}

          {(canManage || template.available) && (
            <div className="flex gap-3">
              {canManage && (
                <Button variant="outline" className="flex-1" size={'sm'} asChild>
                  <Link
                    to={
                      template.kind === 'fixed_docx'
                        ? `/org/${orgSlug}/templates/${template.id}/docx`
                        : `/org/${orgSlug}/templates/${template.id}/edit`
                    }
                  >
                    <Edit className="h-4 w-4" />
                    {t('EDIT')}
                  </Link>
                </Button>
              )}
              {template.available && (
                <Button variant="primary" size={'sm'} className="flex-1" asChild>
                  <Link
                    to={
                      template.kind === 'fixed_docx'
                        ? `/templates/${template.slug}/generate-docx`
                        : `/templates/${template.slug}/generate`
                    }
                  >
                    <Play className="h-4 w-4" />
                    {t('ORGTEMPLATES_USECTA')}
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Desktop layout — original single-row design, unchanged. */}
        <div className="hidden md:flex md:items-center md:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-10 w-10 shrink-0 rounded-lg bg-selise-blue/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-selise-blue" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold truncate">{template.title}</h3>
              <p className="text-sm text-globe-grey truncate">{subtitle}</p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-12">
            <Badge
              variant={template.available ? 'success' : 'secondary'}
              className="gap-1 shrink-0"
            >
              {statusLabel}
            </Badge>

            <div className="flex items-center gap-6">
              {template.available && (
                <Button variant="primary" size="sm" asChild>
                  <Link
                    to={
                      template.kind === 'fixed_docx'
                        ? `/templates/${template.slug}/generate-docx`
                        : `/templates/${template.slug}/generate`
                    }
                  >
                    {t('ORGTEMPLATES_USECTA')}
                  </Link>
                </Button>
              )}
              {canManage && (
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to={
                      template.kind === 'fixed_docx'
                        ? `/org/${orgSlug}/templates/${template.id}/docx`
                        : `/org/${orgSlug}/templates/${template.id}/edit`
                    }
                  >
                    <Edit className="h-4 w-4" />
                    {t('EDIT')}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default TemplateCard;
